import React, { createContext, useContext, useEffect, useState } from 'react';
import { supabase } from '@/integrations/supabase/client';
import type { User, Session } from '@supabase/supabase-js';

interface AuthContextType {
  user: User | null;
  session: Session | null;
  loading: boolean;
  isAdmin: boolean;
  userRole: string | null;
  profile: { display_name: string; avatar_url: string | null; email: string; cover_url?: string | null; country?: string | null; birthdate?: string | null; gender?: string | null } | null;
  signOut: () => Promise<void>;
  refreshProfile: () => Promise<void>;
}

const AuthContext = createContext<AuthContextType>({
  user: null,
  session: null,
  loading: true,
  isAdmin: false,
  userRole: null,
  profile: null,
  signOut: async () => {},
  refreshProfile: async () => {},
});

export const useAuth = () => useContext(AuthContext);

export const AuthProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const [user, setUser] = useState<User | null>(null);
  const [session, setSession] = useState<Session | null>(null);
  const [loading, setLoading] = useState(true);
  const [isAdmin, setIsAdmin] = useState(false);
  const [userRole, setUserRole] = useState<string | null>(null);
  const [profile, setProfile] = useState<AuthContextType['profile']>(null);

  const fetchUserData = async (userId: string) => {
    try {
      // Fetch role
      const { data: roles } = await supabase
        .from('user_roles')
        .select('role')
        .eq('user_id', userId);
      
      if (roles && roles.length > 0) {
        const topRole = roles.find(r => r.role === 'owner')?.role 
          || roles.find(r => r.role === 'admin')?.role
          || roles.find(r => r.role === 'moderator')?.role
          || roles.find(r => r.role === 'editor')?.role
          || roles[0].role;
        setUserRole(topRole);
        setIsAdmin(topRole === 'owner' || topRole === 'admin');
      } else {
        setUserRole(null);
        setIsAdmin(false);
      }

      // Fetch profile
      const { data: prof } = await supabase
        .from('profiles')
        .select('display_name, avatar_url, email, cover_url, country, birthdate, gender')
        .eq('id', userId)
        .maybeSingle();
      
      if (prof) {
        setProfile(prof);
        // Auto-detect country if not set
        if (!prof.country) {
          try {
            const res = await fetch('https://ipapi.co/json/', { signal: AbortSignal.timeout(3000) });
            if (res.ok) {
              const geo = await res.json();
              if (geo.country_name) {
                await supabase.from('profiles').update({ country: geo.country_name } as any).eq('id', userId);
                setProfile(prev => prev ? { ...prev, country: geo.country_name } : prev);
              }
            }
          } catch { /* ignore geo errors */ }
        }
      }
    } catch (err) {
      console.error('Error fetching user data:', err);
    }
  };

  useEffect(() => {
    const { data: { subscription } } = supabase.auth.onAuthStateChange(
      async (_event, session) => {
        setSession(session);
        setUser(session?.user ?? null);
        if (session?.user) {
          setTimeout(() => fetchUserData(session.user.id), 0);
        } else {
          setIsAdmin(false);
          setUserRole(null);
          setProfile(null);
        }
        setLoading(false);
      }
    );

    supabase.auth.getSession().then(({ data: { session } }) => {
      setSession(session);
      setUser(session?.user ?? null);
      if (session?.user) {
        fetchUserData(session.user.id);
      }
      setLoading(false);
    });

    return () => subscription.unsubscribe();
  }, []);

  const signOut = async () => {
    await supabase.auth.signOut();
    setUser(null);
    setSession(null);
    setIsAdmin(false);
    setUserRole(null);
    setProfile(null);
  };

  const refreshProfile = async () => {
    if (user) await fetchUserData(user.id);
  };

  return (
    <AuthContext.Provider value={{ user, session, loading, isAdmin, userRole, profile, signOut, refreshProfile }}>
      {children}
    </AuthContext.Provider>
  );
};
